<template>
  <div class="container-fluid h-100">
    <section
      class="z-index-2"
      v-if="gsapWrapper.loaderOpacity || (useDatastore && !entryLoaded)"
      :style="{ opacity: gsapWrapper.loaderOpacity }"
      id="loading-screen"
      data-cy="loading-screen"
    >
      <div id="loader"></div>
    </section>
    <div class="row h-100">
      <!-- Left sidebar -->
      <div
        class="col-lg-3 col-md-4 h-md-100 overflow-auto px-0"
        v-if="showSidebar"
        v-show="!getMinimizeSidebar"
      >
        <AccordionPanel
          class="z-index-1"
          :sections="getSections"
          :title="'Sample Accordion Panel'"
          :active-section-index="activeSectionIndex"
          :show-progress-bar="true"
          :show-section-warning="showSectionWarning"
        >
          <!-- Custom section template -->
          <template #sectionTemplate="{ steps, sectionIndex }">
            <div
              v-for="(step, stepIndex) in steps"
              :key="stepIndex"
              class="alert w-100 mb-2 d-flex align-items-center"
              :class="{
                'mb-3': stepIndex === steps.length - 1,
                'alert-primary': isCurrentStep(sectionIndex, stepIndex),
                'alert-success': step.completed,
                'alert-secondary':
                  !isCurrentStep(sectionIndex, stepIndex) && !step.completed,
                'cursor-not-allowed': isStepDisabled(sectionIndex, stepIndex)
              }"
            >
              <div class="flex-grow-1" v-html="step.message"></div>
              <a
                v-if="step.instructions"
                tabindex="0"
                class="ms-2 me-2"
                data-bs-toggle="popover"
                data-bs-trigger="hover focus"
                data-bs-placement="bottom"
                :data-bs-content="step.instructions"
                :ref="`popover-trigger-${sectionIndex}-${stepIndex}`"
              >
                <i class="bi bi-question-circle-fill text-secondary"></i>
              </a>
              <!-- Issue: checkbox can't be reliably intercepted by listener, so button is used instead -->
              <div class="ms-2 fs-5">
                <button
                  class="btn btn-sm"
                  :class="{
                    'btn-success': step.completed,
                    'btn-outline-secondary': !step.completed
                  }"
                  :disabled="isStepDisabled(sectionIndex, stepIndex)"
                  @click="toggleStepCompleted(sectionIndex, stepIndex)"
                >
                  <i
                    class="bi"
                    :class="{
                      'bi-check-lg': step.completed,
                      'bi-circle': !step.completed
                    }"
                  ></i>
                </button>
              </div>
            </div>
            <transition name="step-validation">
              <div
                v-if="getErrorMessage"
                class="
                  alert alert-danger
                  border border-danger
                  alert-dismissible
                "
                role="alert"
              >
                {{ getErrorMessage }}
                <button
                  type="button"
                  class="btn-close"
                  aria-label="Close"
                  @click="setErrorMessage('')"
                ></button>
              </div>
            </transition>
            <button
              v-if="canProceedToNextStep(sectionIndex)"
              class="btn btn-lg btn-primary"
              @click="goToNextStep(sectionIndex)"
            >
              {{ getNextButtonText(sectionIndex) }}
            </button>
            <button
              v-if="canProceedToPreviousSection(sectionIndex)"
              class="btn btn-lg btn-secondary mb-2"
              @click="goToPreviousSection(sectionIndex)"
            >
              Previous Section
            </button>
          </template>
        </AccordionPanel>
      </div>
      <!-- Main content area -->
      <div
        class="h-100 position-relative"
        :class="{
          'col-12': getMinimizeSidebar,
          'col-lg-9 col-md-8': !getMinimizeSidebar
        }"
      >
        <!-- Floating image overlay -->
        <!-- TODO: consider making position-fixed if this doesn't work great for mobile -->
        <div
          v-if="getShowDataTable"
          class="
            position-absolute
            top-50
            start-50
            translate-middle
            more-on-top
            floating-image
          "
        >
          <div class="card shadow-lg">
            <div
              class="
                card-header
                d-flex
                justify-content-between
                align-items-center
              "
            >
              <h5 class="mb-0">Field Staking Data Tables</h5>
              <button
                class="btn-close"
                @click="toggleDataTable"
                aria-label="Close"
              ></button>
            </div>
            <div class="card-body">
              <img
                src="@/assets/data-table.jpg"
                alt="Field Staking Data Tables"
                class="img-fluid"
              />
            </div>
          </div>
        </div>
        <ControlInstructionsModal />
        <SurveyingScene @modelsLoaded="loadingComplete" ref="surveyingScene" />
      </div>
    </div>
  </div>
</template>

<script>
import SurveyingScene from "@/components/activity/SurveyingScene";
import AccordionPanel from "@/components/ui/AccordionPanel";
import ControlInstructionsModal from "@/components/activity/ControlInstructionsModal";
import { mapActions, mapGetters, mapState } from "vuex";
import gsap from "gsap";
import stepValidationMixin from "@/mixins/stepValidationMixin";
import DatastoreAPI from "@/helpers/DatastoreAPI";
import { Popover } from "bootstrap";

const POPOVER_OPTIONS = {
  trigger: "hover focus",
  placement: "bottom",
  html: true
};

const activityCompleteMessages = [
  "Congratulations! You have completed the surveying activity. Your progress has been saved for grading by your instructor. Feel free to exit the page now.",
  "You can also redo the activity by unchecking completed steps. This will reset the data that is seen by the instructor, so be sure to complete the entire activity again."
];

export default {
  name: "ActivityView",
  mounted() {
    // Opens the resumed section in the sidebar
    this.activeSectionIndex = this.getCurrentSectionIndex;
  },
  components: {
    SurveyingScene,
    AccordionPanel,
    ControlInstructionsModal
  },
  mixins: [stepValidationMixin, DatastoreAPI],
  data() {
    return {
      loading: true,
      // Wrapper for gsap-animated properties
      gsapWrapper: {
        loaderOpacity: 1
      },
      // Used to track the section that is expanded in the sidebar
      activeSectionIndex: null
    };
  },
  props: {},
  watch: {
    entryLoaded: function (newVal) {
      if (newVal) {
        // Opens the resumed section in the sidebar
        this.activeSectionIndex = this.getCurrentSectionIndex;
      }
    },
    showSidebar: function (newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initPopovers();
          this.showCurrentStepPopover();
        });
      }
    },
    getMinimizeSidebar: function (newVal) {
      // resize the scene to account for the sidebar
      this.$nextTick(() => {
        this.$refs.surveyingScene.updateDimensions();
      });
      if (newVal) {
        this.disposePopovers();
      } else {
        this.initPopovers();
        this.showCurrentStepPopover();
      }
    }
  },
  computed: {
    ...mapGetters([
      "getFlattenedSteps",
      "getAtPointName",
      "getSections",
      "mapToDatastore",
      "getCurrentStepId",
      "getCurrentSectionIndex",
      "getShowDataTable",
      "getMinimizeSidebar",
      "getErrorMessage",
      "useDatastore"
    ]),

    ...mapState("datastore", ["entryLoaded"]),
    showSidebar: function () {
      return !this.loading && (this.useDatastore ? this.entryLoaded : true);
    },
    pointDataExists: function () {
      return this.getAtPointName;
    },
    currentStepIndex: function () {
      const currentSection = this.getSections[this.activeSectionIndex];
      return currentSection?.steps?.findIndex((step) => !step.completed);
    },
    currentSectionIndex: {
      get() {
        return this.getCurrentSectionIndex;
      },
      set(value) {
        this.setCurrentSectionIndex(value);
      }
    }
  },
  beforeMount() {
    // Uncomment if you want to alert user before navigating away
    // window.addEventListener("beforeunload", this.preventNav);
  },
  beforeUnmount() {
    // Uncomment if you want to alert user before navigating away
    // window.removeEventListener("beforeunload", this.preventNav);
    this.disposePopovers();
  },
  methods: {
    ...mapActions([
      "updateStepCompletedByIndex",
      "updatePointPosition",
      "setCurrentSectionIndex",
      "toggleDataTable",
      "updateStepCompletedByID"
    ]),
    showSectionWarning(sectionIndex) {
      return this.getSections[sectionIndex].showWarningFor?.includes(
        this.getCurrentStepId
      );
    },
    loadingComplete: function () {
      this.loading = false;

      gsap.to(this.gsapWrapper, {
        loaderOpacity: 0,
        duration: 1
      });
    },
    hasCompletedAllSteps() {
      return this.getFlattenedSteps.every((step) => step.completed);
    },
    /**
     * Returns the inclusive steps between the current step and the target step
     * @param sectionIndex - the index of the section containing the target step
     * @param stepIndex - the index of the target step
     */
    getIntermediateSteps(sectionIndex, stepIndex) {
      const flattenedSteps = this.getFlattenedSteps;
      const targetStepID = this.getSections[sectionIndex].steps[stepIndex].id;
      const targetStepIndex = flattenedSteps.findIndex(
        (step) => step.id === targetStepID
      );
      let currentStepIndex = flattenedSteps.findIndex(
        (step) => step.id === this.getCurrentStepId
      );

      // ---- Edge Case - Handle when last step has been completed -----------------------------
      // currentStepIndex will be -1 in this case, so we need to check if all steps have been completed
      if (this.hasCompletedAllSteps()) {
        // if all steps have been completed, set the current step index to the last step
        currentStepIndex = flattenedSteps.length - 1;
      }

      return flattenedSteps.slice(targetStepIndex, currentStepIndex + 1);
    },
    toggleStepCompleted(sectionIndex, stepIndex) {
      const step = this.getSections[sectionIndex].steps[stepIndex];
      // get the name of the stake from the step id if the step is a stake-out step
      const getStepStakeName = (stepId) => {
        return stepId?.startsWith("STAKE_OUT")
          ? stepId.replace("STAKE_OUT_", "")
          : null;
      };
      // Hide the popover for the current step, now that interaction is occurring for this step
      this.hideCurrentStepPopover();
      // If the completed step is being reset
      if (step.completed) {
        // Check if any completed steps are after this step
        const intermediateSteps = this.getIntermediateSteps(
          sectionIndex,
          stepIndex
        );
        // If one or more intermediate steps are between the current step and the target step, warn user that they will be resetting multiple steps
        if (intermediateSteps.length > 2) {
          if (
            !window.confirm(
              `This will reset your progress for ${
                intermediateSteps.length - 1
              } steps, are you sure you want to continue?`
            )
          ) {
            return;
          }
        }
        // On confirmation, reset all the completed steps between this step and the current step
        intermediateSteps.forEach((step) => {
          // If the current step is a stake-out step, make sure its stake is removed from the scene and store
          const stakeName = getStepStakeName(step.id);
          if (stakeName) {
            this.$refs.surveyingScene.removeStakeFromScene(stakeName);
            this.updatePointPosition({
              pointName: stakeName,
              position: null
            });
          }
          this.updateStepCompletedByID({
            stepId: step.id,
            completed: false
          });
        });
        // Clear any error messages for the current step
        this.setErrorMessage("");
        // update current section index, since this section now has the earliest uncompleted step
        // (steps after the earliest uncompleted step are always disabled, so this will only ever be for backtracking)
        this.currentSectionIndex = sectionIndex;
        this.activeSectionIndex = sectionIndex;
        // Initialize the popover for the new current step
        this.showCurrentStepPopover();
        // update the datastore with the new progress
        this.updateEntry(this.mapToDatastore);
        return;
      }
      const wasShowingWarning = this.showSectionWarning(sectionIndex);
      // If the step is being completed
      if (this.validateStepCompletion(sectionIndex, stepIndex)) {
        this.updateStepCompletedByIndex({
          sectionIndex,
          stepIndex,
          completed: true
        });
        if (wasShowingWarning) {
          // wait for the warning animation to complete before showing the current step popover, otherwise the popover will be in the wrong position
          setTimeout(() => {
            this.showCurrentStepPopover();
          }, 1000);
        } else {
          this.showCurrentStepPopover();
        }
        // update the datastore with the new progress
        this.updateEntry(this.mapToDatastore);
        // if all steps have been completed, show an alert message
        if (this.hasCompletedAllSteps()) {
          // delay to allow the alert to be shown after the popover is hidden and the checkmark is shown
          // TODO: replace with a modal, we needed a fast messaging solution for now
          setTimeout(() => {
            window.alert(activityCompleteMessages.join("\n\n"));
          }, 500);
        }
      }
    },
    isCurrentStep: function (sectionIndex, stepIndex) {
      // If this step is not disabled and not completed
      return (
        !this.isStepDisabled(sectionIndex, stepIndex) &&
        !this.getSections[sectionIndex].steps[stepIndex].completed
      );
    },
    canProceedToNextStep: function (sectionIndex) {
      return (
        !this.getErrorMessage &&
        !this.isStepDisabled(
          sectionIndex,
          this.getSections[sectionIndex].steps.length - 1
        ) &&
        this.getSections[sectionIndex].steps[
          this.getSections[sectionIndex].steps.length - 1
        ].completed
      );
    },
    canProceedToPreviousSection: function (sectionIndex) {
      if (sectionIndex === 0) {
        return false;
      }
      // check if the current step is the first step of the section
      return !this.getSections[sectionIndex].steps[0].completed;
    },
    getNextButtonText: function (sectionIndex) {
      if (sectionIndex === this.getSections.length - 1) {
        return "Back to Landing Page";
      }
      return `Proceed to ${this.getSections[sectionIndex + 1].title} step`;
    },
    goToNextStep: function (sectionIndex) {
      if (sectionIndex === this.getSections.length - 1) {
        // go back home
        this.$router.push({ name: "Landing" });
        return;
      }
      // hide the current step popover
      this.hideCurrentStepPopover();
      this.activeSectionIndex = sectionIndex + 1;
      this.currentSectionIndex = this.activeSectionIndex;
      // Update the datastore with the new section
      this.updateEntry(this.mapToDatastore);
      // initialize the popover for the new current step, delay to allow accordion to open so the popover is in the correct position
      setTimeout(() => {
        this.showCurrentStepPopover();
      }, 500);
    },
    goToPreviousSection: function (sectionIndex) {
      // hide the current step popover
      this.hideCurrentStepPopover();
      // Set the active section index to the previous section
      this.activeSectionIndex = sectionIndex - 1;
      this.currentSectionIndex = this.activeSectionIndex;
      // Update the datastore with the new section
      this.updateEntry(this.mapToDatastore);
      // initialize the popover for the new current step, delay to allow accordion to open
      //  so the popover is in the correct position
      setTimeout(() => {
        this.showCurrentStepPopover();
      }, 500);
    },
    /**
     * Initialize hover behavior for all popovers on the page
     */
    initPopovers() {
      const popoverTriggerList = [].slice.call(
        document.querySelectorAll('[data-bs-toggle="popover"]')
      );
      popoverTriggerList.map(function (popoverTriggerEl) {
        // Get the id of the section body
        const containerId = `#${popoverTriggerEl.parentElement.parentElement.id}`;
        const options = containerId
          ? {
              ...POPOVER_OPTIONS,
              // if the container id exists, set the container to the section body
              container: containerId
            }
          : POPOVER_OPTIONS;
        return new Popover(popoverTriggerEl, options);
      });
    },
    disposePopovers() {
      const popoverTriggerList = [].slice.call(
        document.querySelectorAll('[data-bs-toggle="popover"]')
      );
      popoverTriggerList.forEach((popoverTriggerEl) => {
        Popover.getInstance(popoverTriggerEl)?.dispose();
      });
    },
    showCurrentStepPopover() {
      if (this.currentStepIndex >= 0) {
        const popoverElements =
          this.$refs[
            `popover-trigger-${this.activeSectionIndex}-${this.currentStepIndex}`
          ];
        if (popoverElements?.length > 0) {
          Popover.getOrCreateInstance(popoverElements[0])?.show();
        }
      }
    },
    hideCurrentStepPopover() {
      const popoverElements =
        this.$refs[
          `popover-trigger-${this.activeSectionIndex}-${this.currentStepIndex}`
        ];
      if (popoverElements?.length > 0) {
        const popover = Popover.getInstance(
          popoverElements[0],
          POPOVER_OPTIONS
        );
        if (popover) {
          popover.hide();
        }
      }
    }
  }
};
</script>

<!-- TODO: Replace with bootstrap spinner -->
<!-- More loader style can be found here: https://freefrontend.com/css-loaders/ -->
<style>
#loading-screen {
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000000;
  opacity: 1;
  /* transition: 1s opacity; */
}

#loading-screen.fadeOut {
  opacity: 0;
}

#loader {
  display: block;
  position: relative;
  left: 50%;
  top: 50%;
  width: 150px;
  height: 150px;
  margin: -75px 0 0 -75px;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: #9370db;
  -webkit-animation: spin 2s linear infinite;
  animation: spin 2s linear infinite;
}

#loader:before {
  content: "";
  position: absolute;
  top: 5px;
  left: 5px;
  right: 5px;
  bottom: 5px;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: #ba55d3;
  -webkit-animation: spin 3s linear infinite;
  animation: spin 3s linear infinite;
}

#loader:after {
  content: "";
  position: absolute;
  top: 15px;
  left: 15px;
  right: 15px;
  bottom: 15px;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: #ff00ff;
  -webkit-animation: spin 1.5s linear infinite;
  animation: spin 1.5s linear infinite;
}

@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
    -ms-transform: rotate(0deg);
    transform: rotate(0deg);
  }

  100% {
    -webkit-transform: rotate(360deg);
    -ms-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
    -ms-transform: rotate(0deg);
    transform: rotate(0deg);
  }

  100% {
    -webkit-transform: rotate(360deg);
    -ms-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

.z-index-1 {
  z-index: 1;
}

.z-index-2 {
  z-index: 2;
}

.step-validation-enter-active,
.step-validation-leave-active {
  transition: all 0.5s ease-in-out;
  overflow: hidden;
}

.step-validation-enter-from,
.step-validation-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.step-validation-enter-to,
.step-validation-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.cursor-not-allowed {
  cursor: not-allowed;
}

.floating-image .card {
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
}

.floating-image .card-body {
  padding: 0;
}
</style>
